/**
 * Header Component
 *
 * Top navigation bar with notification bell (real-time polling),
 * user avatar dropdown (profile, settings, logout), and mobile
 * hamburger menu toggle. Tracks dismissed notification IDs in
 * localStorage.
 *
 * @module components/header
 */

import {
  Bell,
  ClipboardList,
  DollarSign,
  ExternalLink,
  FileText,
  FolderOpen,
  LogOut,
  Menu,
  Settings,
  Upload,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiClient, type DashboardNotification, type PreScreeningRequest } from '../api';
import { useAuth } from '../contexts/auth-context';
import { useEncryption } from '../contexts/EncryptionContext';
import { TourHelpButton } from './TourHelpButton';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface HeaderProps {
  onNavigateToSettings?: () => void;
  onNavigateToRequests?: () => void;
  onNavigateToRequest?: (requestId: string) => void;
  onNavigateToInvoice?: (invoiceId: string) => void;
  onNavigateToCase?: (caseId: string) => void;
  onMobileMenuClick?: () => void;
  onNavigate?: (route: string) => void;
}

// Helpers to persist dismissed intake request IDs
const getDismissedIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem('expertmd_dismissed_request_ids');
    return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
  } catch {
    return new Set<string>();
  }
};
const saveDismissedIds = (ids: Set<string>) => {
  localStorage.setItem('expertmd_dismissed_request_ids', JSON.stringify([...ids]));
};

export function Header({
  onNavigateToSettings,
  onNavigateToRequests,
  onNavigateToRequest,
  onNavigateToInvoice,
  onNavigateToCase,
  onMobileMenuClick,
  onNavigate,
}: HeaderProps) {
  const { user, logout } = useAuth();
  const { decryptField, isUnlocked } = useEncryption();

  // Badge count — lightweight, polled every 60s
  const [badgeCount, setBadgeCount] = useState(0);

  // Dropdown data — fetched on open only
  const [unreadNotifications, setUnreadNotifications] = useState<DashboardNotification[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PreScreeningRequest[]>([]);
  const [dropdownLoaded, setDropdownLoaded] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Track window resize to hide/show mobile menu
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lightweight badge count poll — only fetches counts, not full data
  useEffect(() => {
    const fetchBadgeCount = async () => {
      if (!user) return;

      let requestCount = 0;
      let notifCount = 0;

      // Fetch pending requests count via lightweight endpoint
      try {
        let userIdentifier = user.vanityUrl;
        if (!userIdentifier) {
          try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3005/api/v1';
            const response = await fetch(`${apiUrl}/settings/vanity-url`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
            });
            if (response.ok) {
              const data = await response.json();
              userIdentifier = data.data?.vanityUrl;
            }
          } catch {
            /* ignore */
          }
        }
        if (!userIdentifier) {
          userIdentifier = user.email
            .split('@')[0]
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '-');
        }
        const response = await apiClient.getPendingRequestsCount(userIdentifier);
        // Subtract dismissed count
        const dismissed = getDismissedIds();
        requestCount = Math.max(0, response.count - dismissed.size);
      } catch {
        /* ignore */
      }

      // Fetch unread notifications count
      try {
        const notifications = await apiClient.getNotifications();
        notifCount = notifications.filter((n) => !n.read).length;
      } catch {
        /* ignore */
      }

      setBadgeCount(requestCount + notifCount);
    };

    fetchBadgeCount();
    const interval = setInterval(fetchBadgeCount, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch full dropdown data when opened
  const handleDropdownOpen = async (open: boolean) => {
    if (!open || !user) return;
    setDropdownLoaded(false);

    try {
      const [notifications, requests] = await Promise.all([
        apiClient.getNotifications().catch(() => [] as DashboardNotification[]),
        apiClient.getPreScreeningRequests().catch(() => [] as PreScreeningRequest[]),
      ]);

      setUnreadNotifications(notifications.filter((n) => !n.read));

      const dismissed = getDismissedIds();
      const pending = requests.filter((r) => r.status === 'pending' && !dismissed.has(r.id));

      // Clean up stale dismissed IDs
      const allPendingIds = new Set(
        requests.filter((r) => r.status === 'pending').map((r) => r.id),
      );
      const cleaned = new Set([...dismissed].filter((id) => allPendingIds.has(id)));
      if (cleaned.size !== dismissed.size) saveDismissedIds(cleaned);

      // Decrypt fields
      const decrypted = await Promise.all(
        pending.map(async (r) => {
          try {
            return {
              ...r,
              plaintiff: await decryptField(r.plaintiff, r.caseId ?? undefined),
              firmName: await decryptField(r.firmName, r.caseId ?? undefined),
              attorneyName: await decryptField(r.attorneyName, r.caseId ?? undefined),
              attorneyEmail: await decryptField(r.attorneyEmail, r.caseId ?? undefined),
            };
          } catch {
            return r;
          }
        }),
      );
      setPendingRequests(decrypted);
    } catch {
      // silent fail
    }
    setDropdownLoaded(true);
  };

  const handleMarkNotificationRead = async (id: string) => {
    try {
      await apiClient.markNotificationRead(id);
      setUnreadNotifications((prev) => prev.filter((n) => n.id !== id));
      setBadgeCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silent fail
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await apiClient.markAllNotificationsRead();
    } catch {
      // silent fail
    }
    // Persist dismissed intake request IDs
    const dismissed = getDismissedIds();
    pendingRequests.forEach((r) => {
      dismissed.add(r.id);
    });
    saveDismissedIds(dismissed);
    setUnreadNotifications([]);
    setPendingRequests([]);
    setBadgeCount(0);
  };

  const handleSettingsClick = () => {
    if (onNavigateToSettings) {
      onNavigateToSettings();
    }
  };

  const handleSignOut = () => {
    logout();
  };

  const handleViewIntakeForm = async () => {
    try {
      const data = await apiClient.getVanityUrlSettings();
      let vanityUrl: string | undefined = data.vanityUrl;

      if (!vanityUrl) {
        vanityUrl = user?.vanityUrl;
      }
      if (!vanityUrl && user?.email) {
        vanityUrl = user.email
          .split('@')[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-');
      }

      if (vanityUrl) {
        const baseUrl = window.location.origin;
        window.open(`${baseUrl}/${vanityUrl}`, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('[Header] Failed to fetch vanityUrl:', error);
      const vanityUrl =
        user?.vanityUrl ||
        user?.email
          ?.split('@')[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-');
      if (vanityUrl) {
        const baseUrl = window.location.origin;
        window.open(`${baseUrl}/${vanityUrl}`, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const dropdownItemCount = pendingRequests.length + unreadNotifications.length;

  return (
    <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center shadow-sm">
      {/* Mobile menu button and logo - ONLY visible below 1024px */}
      {isMobile && (
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMobileMenuClick}
            className="text-gray-600 hover:text-gray-900 p-2"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="text-gray-900 font-semibold">ExpertMD</span>
        </div>
      )}

      {/* Right side */}
      <div className="flex items-center gap-2 sm:gap-4 ml-auto">
        {/* Tour Help */}
        <TourHelpButton userRole="doctor" onNavigate={onNavigate} />

        {/* Intake Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewIntakeForm}
          className="flex items-center gap-1 text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900 text-xs sm:text-sm px-2 sm:px-3"
        >
          <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span>Intake</span>
        </Button>

        {/* Notifications Bell Dropdown */}
        <DropdownMenu onOpenChange={handleDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 relative"
            >
              <Bell className="h-5 w-5" />
              {badgeCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center p-0">
                  {badgeCount}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-80 bg-white border-gray-200 text-gray-900 shadow-lg max-h-96 overflow-y-auto"
          >
            <div className="flex items-center justify-between px-3 py-2">
              <DropdownMenuLabel className="text-gray-700 p-0">Notifications</DropdownMenuLabel>
              {dropdownItemCount > 0 && (
                <button
                  type="button"
                  className="text-xs text-[#A66B32] hover:text-[#241610]"
                  onClick={handleMarkAllNotificationsRead}
                >
                  Mark all read
                </button>
              )}
            </div>
            <DropdownMenuSeparator className="bg-gray-200" />
            {!dropdownLoaded ? (
              <div className="py-6 text-center text-sm text-gray-400">Loading...</div>
            ) : dropdownItemCount === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">No notifications</div>
            ) : (
              <>
                {pendingRequests.map((request) => (
                  <DropdownMenuItem
                    key={`req-${request.id}`}
                    className="flex items-start gap-3 p-3 cursor-pointer focus:bg-gray-50"
                    onClick={() => onNavigateToRequest?.(request.id)}
                  >
                    <div className="h-8 w-8 rounded-full bg-[#EAE0CB] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileText className="h-4 w-4 text-[#A66B32]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">New Intake Request</p>
                      <p className="text-sm text-gray-600 truncate">{request.plaintiff}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {new Date(request.dateSubmitted).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </DropdownMenuItem>
                ))}
                {unreadNotifications.map((notification) => {
                  const data = notification.data as Record<string, unknown> | undefined;
                  const notifType = notification.type;

                  // Type-based icon and color
                  let IconComponent = Bell;
                  let iconBg = 'bg-gray-100';
                  let iconColor = 'text-gray-600';
                  if (notifType === 'PAYMENT_RECEIVED' || notifType === 'invoice_sent') {
                    IconComponent = DollarSign;
                    iconBg = 'bg-green-100';
                    iconColor = 'text-green-600';
                  } else if (
                    notifType === 'deliverable_request' ||
                    notifType === 'deliverable_cancelled'
                  ) {
                    IconComponent = ClipboardList;
                    iconBg = 'bg-[#EAE0CB]';
                    iconColor = 'text-[#A66B32]';
                  } else if (
                    notifType === 'case_update' ||
                    notifType === 'case_updated' ||
                    notifType === 'case_status_changed'
                  ) {
                    IconComponent = FolderOpen;
                    iconBg = 'bg-purple-100';
                    iconColor = 'text-purple-600';
                  } else if (notifType === 'file_uploaded') {
                    IconComponent = Upload;
                    iconBg = 'bg-amber-100';
                    iconColor = 'text-amber-600';
                  } else if (notifType === 'intake_request') {
                    IconComponent = FileText;
                    iconBg = 'bg-[#EAE0CB]';
                    iconColor = 'text-[#A66B32]';
                  }

                  return (
                    <DropdownMenuItem
                      key={`notif-${notification.id}`}
                      className="flex items-start gap-3 p-3 cursor-pointer focus:bg-gray-50"
                      onClick={() => {
                        // Type-based navigation
                        const caseId = data?.caseId as string | undefined;
                        const invoiceId = data?.invoiceId as string | undefined;
                        const intakeRequestId = data?.intakeRequestId as string | undefined;

                        if (
                          [
                            'case_update',
                            'deliverable_request',
                            'deliverable_cancelled',
                            'file_uploaded',
                            'case_updated',
                            'case_status_changed',
                            'deliverable_status_changed',
                          ].includes(notifType) &&
                          caseId
                        ) {
                          onNavigateToCase?.(caseId);
                        } else if (notifType === 'intake_request' && intakeRequestId) {
                          onNavigateToRequest?.(intakeRequestId);
                        } else if (
                          ['invoice_sent', 'PAYMENT_RECEIVED'].includes(notifType) &&
                          invoiceId
                        ) {
                          onNavigateToInvoice?.(invoiceId);
                        } else if (caseId) {
                          onNavigateToCase?.(caseId);
                        } else if (invoiceId) {
                          onNavigateToInvoice?.(invoiceId);
                        }
                        handleMarkNotificationRead(notification.id);
                      }}
                    >
                      <div
                        className={`h-8 w-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}
                      >
                        <IconComponent className={`h-4 w-4 ${iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-600 truncate">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(notification.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 sm:gap-3 hover:bg-gray-100 p-2"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={`https://api.dicebear.com/7.x/shapes/svg?seed=${user?.email || 'user'}`}
                  alt={user?.name || 'User'}
                />
                <AvatarFallback className="bg-[#EAE0CB] text-[#3A2718] font-medium text-sm">
                  {user?.name
                    ?.split(' ')
                    .map((n) => n[0])
                    .join('') || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="text-left hidden sm:block">
                <p className="text-gray-900 text-sm font-medium">
                  {user?.title && user?.lastName
                    ? `${user.title} ${user.lastName}`
                    : user?.name || 'User'}
                </p>
                <p className="text-gray-600 text-xs">
                  {user?.role === 'TEAM_MEMBER' && user?.doctorLastName
                    ? `Office of Dr. ${user.doctorLastName}`
                    : user?.specialty || 'Specialty not set'}
                </p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 bg-white border-gray-200 text-gray-900 shadow-lg"
          >
            <DropdownMenuLabel className="text-gray-700">My Account</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-200" />
            <DropdownMenuItem
              className="text-gray-700 focus:bg-gray-100 focus:text-gray-900 cursor-pointer"
              onClick={handleSettingsClick}
            >
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-200" />
            <DropdownMenuItem
              className="text-red-600 focus:bg-red-50 focus:text-red-700 cursor-pointer"
              onClick={handleSignOut}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
